import { useState } from "react";
import { useUI } from "../store/ui";
import { useRH } from "../store/rh";
import { useSettings } from "../store/settings";
import { useTasks } from "../store/tasks";
import { useClientBrand, effectiveBrand } from "../store/clientBrand";
import { PERSONAS } from "../data/org";
import { profileOf } from "../data/profiles";
import { CLIENTS, clientById } from "../data/clients";
import { brainstormThemeAI, type BrainstormFormat, type BrainstormItem } from "../lib/claude";

const FORMATS: { id: BrainstormFormat; label: string; desc: string }[] = [
  { id: "ideias",    label: "💡 Ideias",     desc: "ideias de execução/conteúdo" },
  { id: "hooks",     label: "🎣 Hooks",      desc: "abertura 3s pra reel/vídeo" },
  { id: "headlines", label: "📰 Headlines",  desc: "manchetes de carta/anúncio" },
  { id: "nomes",     label: "🏷️ Naming",    desc: "nomes pra produto/campanha" },
  { id: "conceitos", label: "✨ Conceitos",  desc: "conceitos criativos abrangentes" },
  { id: "roteiros",  label: "🎬 Roteiros",   desc: "micro-roteiros 15-30s" },
  { id: "slogans",   label: "📣 Slogans",    desc: "taglines / slogans" },
  { id: "perguntas", label: "❓ Perguntas",  desc: "provocativas pra pesquisa/conversa" },
  { id: "ofertas",   label: "💎 Ofertas",    desc: "ofertas irrecusáveis estilo Hormozi" },
  { id: "ctas",      label: "▶ CTAs",       desc: "chamadas pra ação" }
];

export function MeetingBrainstorm() {
  const settings = useSettings();
  const picks = useUI(s => s.meetingPicks);
  const employees = useRH(s => s.employees);
  const createTask = useTasks(s => s.create);
  const overrides = useClientBrand(s => s.overrides);

  const [theme, setTheme] = useState("");
  const [clientId, setClientId] = useState("");
  const [format, setFormat] = useState<BrainstormFormat>("ideias");
  const [count, setCount] = useState(8);
  const [toneHint, setToneHint] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<BrainstormItem[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const generate = async () => {
    if (!theme.trim()) { alert("Digita o tema do brainstorm."); return; }
    if (!settings.anthropicKey) { alert("Configure chave Anthropic em ⚙️."); return; }
    setBusy(true);
    setResults([]);
    setSelected(new Set());
    try {
      // pega vozes dos participantes contratados convocados
      const attendees = picks
        .map(id => PERSONAS.find(p => p.id === id))
        .filter((p): p is NonNullable<typeof p> => Boolean(p))
        .filter(p => employees[p.id]?.status === "hired")
        .map(p => ({ name: p.name, voice: profileOf(p.id, p.tier).voice }));

      const cli = clientId ? clientById(clientId) : undefined;
      const eff = cli ? effectiveBrand(cli, overrides[clientId]) : undefined;
      const clientContext = cli && eff
        ? `${cli.name} (${cli.segment}). Público: ${eff.audience}.` +
          (eff.voiceTone ? ` Tom: ${eff.voiceTone}.` : "") +
          (eff.persona ? ` Persona: ${eff.persona}.` : "")
        : undefined;

      const items = await brainstormThemeAI({
        apiKey: settings.anthropicKey,
        model: settings.model,
        theme, format, count,
        attendeeNames:  attendees.map(a => a.name),
        attendeeVoices: attendees.map(a => a.voice),
        clientContext,
        toneHint: toneHint.trim() || undefined
      });
      setResults(items);
    } catch (e) {
      alert("Falha: " + (e as Error).message);
    } finally { setBusy(false); }
  };

  const toggle = (i: number) =>
    setSelected(s => {
      const n = new Set(s);
      n.has(i) ? n.delete(i) : n.add(i);
      return n;
    });

  const selectedItems = [...selected].sort().map(i => results[i]).filter(Boolean);

  const copySelected = () => {
    if (selectedItems.length === 0) { alert("Selecione pelo menos uma opção."); return; }
    const text = selectedItems.map(it => `▸ ${it.title}${it.detail ? `\n   ${it.detail}` : ""}`).join("\n\n");
    navigator.clipboard.writeText(text);
    alert(`${selectedItems.length} item(s) copiado(s).`);
  };

  const saveAsTasks = () => {
    if (selectedItems.length === 0) { alert("Selecione pelo menos uma opção."); return; }
    selectedItems.forEach(it => {
      createTask({
        title: `[${format}] ${it.title}`,
        description: `${it.detail ?? ""}\n\n— do brainstorm sobre: "${theme}"`,
        clientId: clientId || undefined,
        targetDepts: [],
        assignedTo: [],
        priority: "normal",
        tags: ["brainstorm", format],
        status: "pending",
        stage: "draft"
      });
    });
    alert(`✓ ${selectedItems.length} demanda(s) criada(s) no Quadro.`);
    setSelected(new Set());
  };

  return (
    <div className="space-y-3">
      {/* HEADER */}
      <div className="border-2 border-ink bg-red/10 px-4 py-3">
        <div className="font-display font-bold uppercase">🎨 Brainstorm Criativo</div>
        <div className="font-crimson italic text-paper text-sm">
          define um tema, escolhe o formato, recebe {count} opções como BOTÕES selecionáveis.
          Os participantes convocados ({picks.length}) emprestam a voz.
        </div>
      </div>

      {/* CONTROLES */}
      <section className="border-2 border-ink p-4 bg-bone space-y-3">
        <div>
          <div className="tag text-graydk mb-1">tema do brainstorm</div>
          <input
            value={theme}
            onChange={(e) => setTheme(e.target.value)}
            placeholder="ex: lançamento Black Friday Mentoria VPC · R$ 200k em mídia · 30 dias"
            className="w-full border border-ink bg-bone px-3 py-2 font-display font-bold text-lg uppercase placeholder:text-graymd placeholder:normal-case placeholder:italic placeholder:text-sm placeholder:font-serif"
          />
        </div>

        <div>
          <div className="tag text-graydk mb-2">formato (clique pra escolher)</div>
          <div className="flex flex-wrap gap-1.5">
            {FORMATS.map(f => (
              <button
                key={f.id}
                onClick={() => setFormat(f.id)}
                className={`btn ${format === f.id ? "bg-ink text-bone border-ink" : "btn-fire"}`}
                title={f.desc}>
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-2">
          <div>
            <div className="tag text-graydk mb-1">cliente (opcional)</div>
            <select value={clientId} onChange={(e) => setClientId(e.target.value)}
              className="w-full border border-ink bg-bone px-2 py-1 font-mono text-[12px]">
              <option value="">— sem cliente —</option>
              {CLIENTS.map(c => <option key={c.id} value={c.id}>{c.shortName}</option>)}
            </select>
          </div>
          <div>
            <div className="tag text-graydk mb-1">quantidade</div>
            <input type="number" min={3} max={20} value={count}
                   onChange={(e) => setCount(Number(e.target.value))}
                   className="w-full border border-ink bg-bone px-2 py-1 font-mono text-[12px]" />
          </div>
          <div>
            <div className="tag text-graydk mb-1">direcionamento extra (opcional)</div>
            <input value={toneHint}
                   onChange={(e) => setToneHint(e.target.value)}
                   placeholder="ex: tom irreverente, foco em prova social"
                   className="w-full border border-ink bg-bone px-2 py-1 font-mono text-[12px]" />
          </div>
        </div>

        <button onClick={generate} disabled={busy}
          className="btn bg-red text-bone border-red w-full py-3 text-base disabled:opacity-50">
          {busy ? "🎨 gerando…" : `🎨 GERAR ${count} ${FORMATS.find(f => f.id === format)?.label.toUpperCase()}`}
        </button>
      </section>

      {/* RESULTADOS COMO BOTÕES SELECIONÁVEIS */}
      {results.length > 0 && (
        <>
          <div className="border-2 border-ink bg-bone px-4 py-2 flex items-center justify-between sticky top-0 z-10">
            <div>
              <span className="font-display font-bold uppercase">
                {results.length} opções · {selected.size} selecionada{selected.size === 1 ? "" : "s"}
              </span>
              <span className="font-crimson italic text-paper text-sm ml-2">clique nos cards pra escolher</span>
            </div>
            <div className="flex gap-2">
              {selected.size > 0 && (
                <>
                  <button onClick={copySelected} className="btn btn-fire">📋 copiar</button>
                  <button onClick={saveAsTasks} className="btn bg-ink text-bone border-ink">
                    ↳ virar {selected.size} demanda{selected.size === 1 ? "" : "s"}
                  </button>
                </>
              )}
              <button onClick={() => setSelected(new Set(results.map((_, i) => i)))}
                className="tag underline">todos</button>
              <button onClick={() => setSelected(new Set())}
                className="tag underline">limpar</button>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-2">
            {results.map((item, i) => {
              const isSel = selected.has(i);
              return (
                <button
                  key={i}
                  onClick={() => toggle(i)}
                  className={`text-left border-2 p-3 transition
                    ${isSel
                      ? "border-red bg-red/10 shadow-[3px_3px_0_0_#BC2A24]"
                      : "border-ink bg-bone hover:bg-ink/5"}`}>
                  <div className="flex items-start gap-2 mb-1">
                    <span className={`w-5 h-5 border-2 ${isSel ? "border-red bg-red" : "border-ink"} flex items-center justify-center shrink-0 mt-0.5`}>
                      {isSel && <span className="text-bone text-xs leading-none">✓</span>}
                    </span>
                    <span className="tag text-graydk shrink-0">#{String(i+1).padStart(2,"0")}</span>
                    {item.tag && <span className="tag bg-gold text-ink px-1.5">{item.tag}</span>}
                  </div>
                  <div className="font-display font-bold uppercase text-base leading-tight mb-1">
                    {item.title}
                  </div>
                  {item.detail && (
                    <div className="font-crimson italic text-paper text-[13px] leading-snug">
                      {item.detail}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
